/**
 * StockMovementsTable - Tabela compartilhada do histórico do Estoque.
 * RELEASE: 2026-10-10 v1.5.0 - Coluna opcional de direção (showDirection): seta verde
 *            para cima (entrada no veículo) e vermelha para baixo (saída). Usada apenas
 *            na aba Equipamentos do modal Veículo; demais listagens inalteradas.
 * RELEASE: 2026-10-09 v1.3.0 - Cores Cliente/Estoque/Prestador por tokens e linhas mais baixas.
 *
 * Mantém a mesma apresentação na página geral e na ficha individual do produto,
 * evitando divergência entre datas, etiquetas, locais e observações.
 *
 * RELEASE: 2026-10-09 v1.2.0 - Etiqueta de tipo de produto com cor própria por tipo (Kit azul).
 * RELEASE: 2026-10-09 v1.1.0 - Inclui N. OS e compacta tipografia e etiquetas de Produto.
 * RELEASE: 2026-10-08 v1.0.0 - Extração da tabela reutilizável de movimentações.
 */
import { ArrowDown, ArrowRight, ArrowRightLeft, ArrowUp, Clock3, Unlink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StockPlaceLabel } from '@/components/stock/StockPlaceLabel';
import type { StockMovementRow } from '@/hooks/useStockMovements';
import { cn } from '@/lib/utils';
import {
  formatStockDateTime,
  stockMovementDestinationLabel,
  stockMovementDetails,
  stockProductTypeBadgeClass,
  stockProductTypeLabel,
  stockMovementSourceLabel,
  stockServiceOrderReference,
} from '@/lib/stock-presentation';

const DESTINATION_BADGE = {
  // Cores do modelo de referência via tokens semânticos (src/index.css)
  location: 'bg-place-location text-place-location-foreground',
  provider: 'bg-place-provider text-place-provider-foreground',
  customer: 'bg-place-customer text-place-customer-foreground',
  // Logística: item com a transportadora
  carrier: 'bg-muted text-muted-foreground',
} as const;

interface Props {
  /** Linhas do histórico; `direction` (opcional) é usado quando showDirection está ativo. */
  items: (StockMovementRow & { direction?: 'in' | 'out' })[];
  isLoading: boolean;
  compact?: boolean;
  /** v1.5.0: exibe a primeira coluna com seta de direção (entrada/saída no veículo). */
  showDirection?: boolean;
}

/** Exibe o histórico com colunas idênticas em qualquer contexto. */
export function StockMovementsTable({ items, isLoading, compact = false, showDirection = false }: Props) {
  if (isLoading) return <Skeleton className="h-40 w-full rounded-lg" />;

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        <ArrowRightLeft className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Nenhuma movimentação encontrada.</p>
      </div>
    );
  }

  return (
    // Tabela sem viewport interno: quem rola verticalmente é a página (padrão Veículos);
    // o wrapper nativo do Table mantém a rolagem horizontal em telas estreitas
    <Table className={cn(
      showDirection ? (compact ? 'min-w-[1150px]' : 'min-w-[1340px]') : (compact ? 'min-w-[1120px]' : 'min-w-[1310px]'),
      '[&_td]:py-2.5',
    )}>
        <TableHeader>
          <TableRow>
            {/* Coluna de direção: seta verde (entrada) / vermelha (saída) — só no veículo */}
            {showDirection && (
              <TableHead className="w-10 text-xs">
                <span className="sr-only">Movimento</span>
              </TableHead>
            )}
            <TableHead className="w-40 text-xs">Data</TableHead>
            <TableHead className="w-44 text-xs">Produto</TableHead>
            <TableHead className="w-52 text-xs">Item</TableHead>
            <TableHead className="w-36 text-xs">N. OS</TableHead>
            <TableHead className="text-xs">De</TableHead>
            <TableHead className="w-6 text-xs" />
            <TableHead className="text-xs">Para</TableHead>
            <TableHead className="w-36 text-xs">Por</TableHead>
            <TableHead className="w-52 text-xs">Observações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((movement) => (
            <TableRow key={movement.id}>
              {/* Seta de direção: verde p/ cima = entrada no veículo; vermelha p/ baixo = saída */}
              {showDirection && (
                <TableCell className="text-center">
                  {movement.direction === 'in' && (
                    <span title="Entrada no veículo"><ArrowUp className="h-4 w-4 text-emerald-500" aria-label="Entrada no veículo" /></span>
                  )}
                  {movement.direction === 'out' && (
                    <span title="Saída do veículo"><ArrowDown className="h-4 w-4 text-destructive" aria-label="Saída do veículo" /></span>
                  )}
                  {!movement.direction && <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
              )}
              <TableCell className="whitespace-nowrap text-xs tabular-nums">
                <span className="flex items-center gap-1.5">
                  <Clock3 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  {formatStockDateTime(movement.created_at)}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap">
                  <Badge variant="secondary" className={cn('h-5 shrink-0 border-0 px-2 text-[9px] font-semibold', stockProductTypeBadgeClass(movement.item_type))}>
                    {stockProductTypeLabel(movement.item_type)}
                  </Badge>
                  {movement.to_place && (
                    <Badge variant="outline" className={cn('h-5 shrink-0 border-0 px-2 text-[9px] font-medium', DESTINATION_BADGE[movement.to_place.type])}>
                      {stockMovementDestinationLabel(movement.to_place.type)}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="truncate tabular-nums text-xs font-medium">{movement.kit_code ?? movement.item_label}</div>
                {stockMovementDetails(movement).map((detail) => (
                  <div key={detail} className="truncate text-[10px] tabular-nums text-muted-foreground">{detail}</div>
                ))}
                {movement.event_type === 'kit_dissolved' && (
                  <div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
                    <Unlink className="h-3 w-3" /> Desmontagem de kit
                  </div>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                {stockServiceOrderReference(movement.source_type, movement.source_reference)}
                {/* Tipo de serviço da OS logo abaixo do número (quando a movimentação veio de OS) */}
                {movement.service_type_name && (
                  <div className="truncate text-[10px] text-muted-foreground/80">{movement.service_type_name}</div>
                )}
              </TableCell>
              <TableCell><StockPlaceLabel place={movement.from_place} compact /></TableCell>
              <TableCell><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /></TableCell>
              <TableCell><StockPlaceLabel place={movement.to_place} compact /></TableCell>
              <TableCell className="truncate text-xs text-muted-foreground">{movement.created_by_name ?? '—'}</TableCell>
              <TableCell className="max-w-52 text-xs text-muted-foreground">
                <p className="line-clamp-2 break-words" title={movement.notes ?? undefined}>{movement.notes || '—'}</p>
                {/* BR-OS-001: origem do pedido e documento vinculado */}
                <p className="mt-0.5 truncate text-[10px] text-muted-foreground/80">
                  {stockMovementSourceLabel(movement.source_type, movement.source_reference)}
                </p>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
    </Table>
  );
}